import React, { useEffect, useRef, useState } from 'react';
import { Space, Typography } from 'antd';
import { useVinCode } from '../../common/hooks/useVinCode';
import { useNavigate, useSearchParams } from 'react-router-dom';
import VinCodeInput from './vinCodeInput';
import classes from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { validateVinCode } from '../../utils/vinValidation';
import ReactGA from 'react-ga4';
import { googleAnalyticsEvents } from '../../common/constants/googleAnalyticsEvents';
import { useReportService } from '../../services/reportService';
import { FULL_ACCESS_PARAM, LANDING_VIN_PARAM, openLandingVehiclePage } from '../../utils/landingHandoff';

const VinCodeForm = ({ vinCode }) => {
    const { t, i18n } = useTranslation(['profile']);
    const { code, error, handleInputChange, setCode, setError } = useVinCode({ vinCode, t });
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const { checkReportAvailability, getVehiclePreview } = useReportService();

    const landingVinConsumedRef = useRef(false);
    // set synchronously, so a second click before the disabled button re-renders is ignored
    const lookupRunningRef = useRef(false);
    const navigate = useNavigate();

    useEffect(() => {
        const vincode = searchParams.get('vinCode');
        const landingVin = searchParams.get(LANDING_VIN_PARAM);
        const purchasedPlan = searchParams.get('purchasedPlan');

        if (vincode) {
            setCode(vincode);
        }

        if (purchasedPlan) {
            ReactGA.event(googleAnalyticsEvents.PAYMENT_COMPLETED, {
                plan_name: purchasedPlan,
            });

            setSearchParams(prev => {
                prev.delete('purchasedPlan');
                return prev;
            });
        }

        if (!landingVin || landingVinConsumedRef.current) {
            return;
        }

        landingVinConsumedRef.current = true;

        const normalizedVin = landingVin.toUpperCase().trim();

        if (validateVinCode(normalizedVin, t)) {
            return;
        }

        setCode(normalizedVin);

        // "Get FULL Access" on the landing preview page: the preview was already seen, go on to the plans
        if (searchParams.get(FULL_ACCESS_PARAM) === 'true') {
            goToPlans(normalizedVin, { fromLanding: true });
        }
    }, [searchParams]);

    const goToPlans = async (code, { fromLanding = false } = {}) => {
        setIsLoading(true);

        const baseUrl = `/select-report?vinCode=${encodeURIComponent(code)}`;
        // coming from the landing, the dashboard entry is replaced so Back returns to the landing instead of
        // re-running this redirect
        const navigateOptions = { replace: fromLanding };

        try {
            const data = await checkReportAvailability(code);

            if (!fromLanding) {
                setSearchParams(prev => {
                    const params = new URLSearchParams(prev);
                    params.set('vinCode', code);
                    return params;
                });
            }

            if (data?.hasInternalReport) {
                navigate(baseUrl, navigateOptions);
            } else {
                navigate(`${baseUrl}&externalOnly=true`, navigateOptions);
            }
        } catch (err) {
            console.error('Availability check failed:', err?.message);
            navigate(`${baseUrl}&externalOnly=true`, navigateOptions);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFormSubmit = async code => {
        const validationError = validateVinCode(code, t);

        if (validationError) {
            setError(validationError);
            ReactGA.event(googleAnalyticsEvents.CHECK_VIN_CLICK_INVALID);
            return;
        }

        if (lookupRunningRef.current) {
            return;
        }

        lookupRunningRef.current = true;
        setIsLoading(true);
        ReactGA.event(googleAnalyticsEvents.CHECK_VIN_CLICK_VALID);

        try {
            // a car that is in the base gets its landing preview page first; its "Get FULL Access" leads to the plans
            const preview = await getVehiclePreview(code);

            if (preview) {
                // reset before leaving so the button is not stuck disabled if the page is restored from bfcache
                setIsLoading(false);
                openLandingVehiclePage(code, i18n.language);
                return;
            }

            await goToPlans(code);
        } finally {
            lookupRunningRef.current = false;
        }
    };

    return (
        <Space.Compact className={classes['vin_code_input']}>
            <VinCodeInput
                code={code}
                isLoading={isLoading}
                onChange={handleInputChange}
                onSubmit={code => handleFormSubmit(code)}
            />

            {error && <Typography className={classes['error-message']}>{error}</Typography>}
        </Space.Compact>
    );
};

export default VinCodeForm;
